import { ActionTree } from 'vuex'
import * as types from './mutation-types'
import RootState from '@vue-storefront/core/types/RootState'
import UserState from '../../types/UserState'
import { buildApiUrl } from '../../helpers'
import fetch from 'isomorphic-fetch'
import config from 'config'
import { customerAuthHeaders } from '@vue-storefront/core/modules/user/helpers/customerSession'

// K2-3073: vuestorefront-api acts on iPay only for the customer token - it takes
// the customer, phone and amount from ms-api, not from these parameters (still
// sent for an older vuestorefront-api).
// K2-3138: the token goes as a Bearer header; a `token` a caller still passes is dropped from the URL.
export function withoutCustomerToken (params = {}) {
  const { token, ...rest } = params as any
  return rest
}

export function ipayHeaders (rootState) {
  return Object.assign({
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }, customerAuthHeaders(rootState.user && rootState.user.token))
}

// K2-3073 (e1 review): the OTP confirmation's loader never spins forever. Past
// this the outcome is unknown - answered as vuestorefront-api's 202 (being
// checked), never as a failure. vuestorefront-api bounds its own iPay and
// ms-api calls; the proxies in front of it give up at 60 s too.
export const VERIFY_2DS_TIMEOUT_MS = 60000

const actions: ActionTree<UserState, RootState> = {
  async createTransaction ({ rootState }, payload) {
    // K2-3073: the payment is recorded only for the token's own order. Rejects
    // unless it is recorded, so the checkout never takes a 503/422 as done.
    const resp = await fetch(buildApiUrl(config.orders.create_transaction_endpoint, {}), {
      method: 'POST',
      mode: 'cors',
      headers: ipayHeaders(rootState),
      body: JSON.stringify(payload)
    })
    const body = await resp.json().catch(() => ({}))
    if (!resp.ok) {
      throw { ...body, code: resp.status }
    }
    return body
  },
  async getUserStatus ({ commit, rootState }, payload) {
    return await fetch(buildApiUrl(config.ipay.user.get_status_endpoint, withoutCustomerToken(payload)), {
      method: 'GET',
      mode: 'cors',
      headers: ipayHeaders(rootState)
    })
    .then(resp => resp.json())
    .then(resp => {
      let result = resp.result.user_status ? resp.result.user_status : resp.result
      commit(types.SET_USER_STATUS, result)
      return resp
    })
    .catch(err => console.error(err))
  },
  async getCardsList ({ commit, rootState }, payload) {
    return await fetch(buildApiUrl(config.ipay.user.get_cards_list_endpoint, withoutCustomerToken(payload)), {
      method: 'GET',
      mode: 'cors',
      headers: ipayHeaders(rootState)
    })
      .then(resp => resp.json())
      .then(resp => {
        commit(types.SET_USER_CARDS, resp)
        return resp
      })
      .catch(err => console.error(err))
  },
  async getRegisterLink ({ rootState }, payload) {
    return await fetch(buildApiUrl(config.ipay.user.get_register_link_endpoint, withoutCustomerToken(payload)), {
      method: 'GET',
      mode: 'cors',
      headers: ipayHeaders(rootState)
    })
      .then(resp => resp.json())
      .catch(err => console.error(err))
  },
  async getInviteLink ({ rootState }, payload) {
    return await fetch(buildApiUrl(config.ipay.user.get_invite_link_endpoint, withoutCustomerToken(payload)), {
      method: 'GET',
      mode: 'cors',
      headers: ipayHeaders(rootState)
    })
      .then(resp => resp.json())
      .catch(err => console.error(err))
  },
  async getAddCardLink ({ rootState }, payload) {
    return await fetch(buildApiUrl(config.ipay.user.get_add_card_link_endpoint, withoutCustomerToken(payload)), {
      method: 'GET',
      mode: 'cors',
      headers: ipayHeaders(rootState)
    })
      .then(resp => resp.json())
      .catch(err => console.error(err))
  },
  async createPayment({ rootState }, payload) {
    return await fetch(buildApiUrl(config.ipay.user.get_create_payment_endpoint, {}), {
      method: 'POST',
      mode: 'cors',
      headers: ipayHeaders(rootState),
      body: JSON.stringify(payload)
    })
      .then(resp => resp.json())
      .catch(err => console.error(err))
  },
  async verify3DS({}, payload) {
    return await fetch(buildApiUrl(config.ipay.user.verify_threeds_endpoint, payload), {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })
      .then(resp => resp.json())
      .catch(err => console.error(err))
  },
  toggleOtpModal ({ commit }) {
    commit(types.TOGGLE_OTP_MODAL)
  },
  async verify2DS({ rootState }, payload) {
    // K2-3072: token stays the OTP token (as older vuestorefront-api reads it);
    // the customer token - it must own the order - goes as a Bearer header (K2-3138).
    const params = payload
    // AbortController where the browser has it (the fetch polyfill honours its
    // signal); the race alone bounds the wait elsewhere.
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
    let timer
    const timedOut = new Promise(resolve => {
      timer = setTimeout(() => {
        resolve({ code: 202, timeout: true })
        if (controller) {
          controller.abort()
        }
      }, VERIFY_2DS_TIMEOUT_MS)
    })
    const verified = fetch(buildApiUrl(config.ipay.payment.verify_twods_endpoint, params), {
      method: 'GET',
      mode: 'cors',
      headers: ipayHeaders(rootState),
      ...(controller ? { signal: controller.signal } : {})
    })
      .then(resp => resp.json())
    try {
      return await Promise.race([verified, timedOut])
    } finally {
      clearTimeout(timer)
    }
  },
  async selectCard({ commit, rootState }, payload) {
    return await fetch(buildApiUrl(config.ipay.user.get_select_card_endpoint, withoutCustomerToken(payload)), {
      method: 'GET',
      mode: 'cors',
      headers: ipayHeaders(rootState)
    })
      .then(resp => resp.json())
      .then(resp => {
        commit(types.SELECT_USER_CARD, resp.name)
        return resp
      })
      .catch(err => console.error(err))
  },
  async deleteCard({ commit, rootState }, payload) {
    return await fetch(buildApiUrl(config.ipay.user.get_delete_card_endpoint, withoutCustomerToken(payload)), {
      method: 'GET',
      mode: 'cors',
      headers: ipayHeaders(rootState)
    })
      .then(resp => resp.json())
      .then(async resp => {
        if (resp.result && resp.result.status === 'OK') {
          await commit(types.DELETE_USER_CARD, payload.cardName)
          if (resp.data && resp.data.name) {
            await commit(types.SELECT_USER_CARD, resp.data.name)
          }
        }
        return resp
      })
      .catch(err => console.error(err))
  },
  async calculateFee ({ commit, rootState }, payload) {
    if (!payload.amount) {
      return
    }
    if (!rootState.user || !rootState.user.token) {
      // Only a signed-in customer pays online.
      commit(types.IPAY_FEE, 0)
      return
    }
    commit(types.FEE_CALCULATION_RUNNING, true)
    return await fetch(buildApiUrl(config.ipay.payment.calculate_amount, withoutCustomerToken(payload)), {
      method: 'GET',
      mode: 'cors',
      headers: ipayHeaders(rootState)
    })
      .then(resp => resp.json())
      .then(result => {
        if (result.code != 200) {
          commit(types.IPAY_FEE, 0)
          return result
        }
        commit(types.IPAY_FEE, ((result.result.amount - result.result.invoice) / 100))
        return result
      })
      .catch(err => console.error(err))
      .finally(() => commit(types.FEE_CALCULATION_RUNNING, false))
  },
  async failedTransaction ({ rootState }, payload) {
    // K2-3072: only the token's own order can be marked failed.
    return await fetch(buildApiUrl(config.ipay.payment.failed_payment_endpoint, withoutCustomerToken(payload)), {
      method: 'POST',
      mode: 'cors',
      headers: ipayHeaders(rootState)
    })
      .then(resp => resp.json())
      .catch(err => console.error(err))
  },
  resetFee({ commit }) {
    commit(types.IPAY_FEE, 0)
  },
  clearCardsList({ commit }) {
    commit(types.SET_USER_CARDS, [])
  },
  async loadIPayActionLink({ getters, dispatch, commit }, payload) {
    let result
    commit(types.SET_IPAY_ACTION_LINK_RUNNING, true)
    switch (getters.getUserStatus) {
      case 'exists':
        result = await dispatch('getAddCardLink', payload)
        break;
      case 'invite':
        result = await dispatch('getInviteLink', payload)
        break;
      default:
        result = await dispatch('getRegisterLink', payload)
        break;
    }

    if (result.code === 200 && result.result && result.result.url) {
      commit(types.SET_IPAY_ACTION_LINK, result.result.url)
      commit(types.SET_IPAY_ACTION_LINK_RUNNING, false)
      return result.result.url
    }
    throw new Error('Unable to retrieve iPay Action Link')
  },
  openIPayActionLink ({ getters }) {
    if (getters.getIPayActionLink && window) {
      window.open(getters.getIPayActionLink, '_self')
    }
  }
}

export default actions
